<script>
    // ===============================
    // IMPORTS
    // ===============================
    import ArrowDownAZ from 'lucide-svelte/icons/arrow-down-a-z';
    import ArrowDownUp from 'lucide-svelte/icons/arrow-down-up';
    import ArrowUpZA from 'lucide-svelte/icons/arrow-up-z-a';
    import Check from 'lucide-svelte/icons/check';
    import ListFilter from 'lucide-svelte/icons/list-filter';
    import Search from 'lucide-svelte/icons/search';
    import X from 'lucide-svelte/icons/x';
    import { createEventDispatcher, onMount } from 'svelte';

    // ===============================
    // EVENT DISPATCHER
    // ===============================
    const dispatch = createEventDispatcher();

    // ===============================
    // COMPONENT PROPS
    // ===============================
    export let header;
    export let headerOptions;
    export let sortDirection;
    export let filters;
    export let filterOptions = [];
    export let selectedFilters = new Set();
    export let checkboxSearchQueries = {};
    export let minValue = 0;
    export let maxValue = 100;

    // ===============================
    // LOCAL STATE
    // ===============================
    let isVisible = false;
    let searchInput;
    let filterMode = filters[header]?.filterMode ?? 'Between';
    let sliderMin = minValue;
    let sliderMax = maxValue;
    let sliderLeft, sliderRight;

    // ===============================
    // REACTIVE STATEMENTS
    // ===============================
    $: {
        sliderLeft = `${((sliderMin - minValue) / (maxValue - minValue)) * 100}%`;
        sliderRight = `${100 - ((sliderMax - minValue) / (maxValue - minValue)) * 100}%`;
        document.documentElement.style.setProperty('--left', sliderLeft);
        document.documentElement.style.setProperty('--right', sliderRight);
    }

    // ===============================
    // LIFECYCLE METHODS
    // ===============================
    onMount(() => {
        requestAnimationFrame(() => {
            isVisible = true; // Trigger animation

            if (searchInput) {
                searchInput.focus();
            }
        });

        document.documentElement.classList.add('no-scroll');
    });

    // ===============================
    // EVENT HANDLERS
    // ===============================
    const handleClose = () => {
        isVisible = false;
        setTimeout(() => {
            dispatch('close');

            document.documentElement.classList.remove('no-scroll');
        }, 300);
    };

    const handleSearch = (query) => {
        dispatch('search', { header, query: query.toLowerCase().trim() });
    };

    const handleCheckboxSearch = (query) => {
        console.log(query);
        checkboxSearchQueries = { ...checkboxSearchQueries, [header]: query.toLowerCase() };
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            filterOptions.forEach((option) => selectedFilters.add(option));
        } else {
            selectedFilters.clear();
        }

        filterOptions.forEach((option) => {
            dispatch('filter', { value: option, checked });
        });
    };

    const handleFilterChange = (value, checked) => {
        dispatch('filter', { value, checked });
    };

    const handleRangeChange = () => {
        dispatch('rangeFilter', {
            header,
            filterMode,
            minValue: sliderMin,
            maxValue: sliderMax,
        });
    };

    const handleSortChange = (direction) => {
        dispatch('sort', { header, direction });
    };

    const handleFilterModeChange = (e) => {
        filterMode = e.target.value;
        handleRangeChange();
    };
</script>

<div
    role="button"
    tabindex="-1"
    aria-label="Close filter menu"
    class="overlay {isVisible ? 'visible' : 'hidden'}"
    on:click={handleClose}
    on:keydown={(e) => e.key === 'Escape' && handleClose()}
>
    <div
        role="button"
        tabindex="-1"
        class="mobile-filter-menu {isVisible ? 'slide-up' : 'slide-down'} {headerOptions[header]?.filterable
            ? 'filterable'
            : ''}"
        on:click|stopPropagation
        on:keydown={(e) => e.key === 'Escape' && handleClose()}
    >
        <div class="menu-header">
            <button class="close-button" aria-label="Close menu" on:click={handleClose}><X /></button>
        </div>

        <!-- Search Input -->
        {#if headerOptions[header]?.searchable}
            <div class="menu-section">
                <div class="search-container">
                    <Search />
                    <input
                        type="text"
                        inputmode="search"
                        placeholder="Search …"
                        bind:this={searchInput}
                        on:input={(e) => handleSearch(e.target.value)}
                    />
                </div>
            </div>
        {/if}

        <!-- Checkbox Filters -->
        {#if headerOptions[header]?.filterable}
            <div class="menu-section">
                <div class="search-container">
                    <Search />
                    <input
                        type="text"
                        inputmode="search"
                        placeholder="Search …"
                        bind:this={searchInput}
                        on:input={(e) => handleCheckboxSearch(e.target.value)}
                    />
                </div>

                <!-- Select/Deselect All -->
                <div class="select-toggle-container">
                    <button class="select-toggle-button" on:click={() => handleSelectAll(true)}>
                        <Check /> Select All
                    </button>
                    <button class="select-toggle-button" on:click={() => handleSelectAll(false)}>
                        <X /> Clear
                    </button>
                </div>

                <ul class="select-options-list">
                    {#each filterOptions as option}
                        <li>
                            <label for={header + '-' + option}>
                                <input
                                    type="checkbox"
                                    id={header + '-' + option}
                                    checked={selectedFilters.has(option)}
                                    on:change={(e) => handleFilterChange(option, e.target.checked)}
                                />
                                {option}
                            </label>
                        </li>
                    {/each}
                </ul>
            </div>
        {/if}

        <!-- Range Filters -->
        {#if headerOptions[header]?.sortable}
            <div class="menu-section">
                <!-- Sort Options (Ascending/Descending) -->
                <p>
                    <ArrowDownUp /> Sort:
                </p>

                <div class="sort-toggle-container">
                    <button
                        on:click={() => handleSortChange('asc')}
                        class="sort-toggle-button {sortDirection === 'asc' ? 'active' : ''}"
                        ><ArrowDownAZ /> Sort Ascending</button
                    >
                    <button
                        on:click={() => handleSortChange('desc')}
                        class="sort-toggle-button {sortDirection === 'desc' ? 'active' : ''}"
                        ><ArrowUpZA /> Sort Descending</button
                    >
                </div>

                {#if headerOptions[header]?.filterable}
                    <!-- Range Slider Toggle -->
                    <p><ListFilter /> Filter:</p>
                    <div class="range-mode-toggle-container">
                        <select bind:value={filterMode} on:change={handleFilterModeChange}>
                            <option value="Between">Between</option>
                            <option value="Equals">Equals</option>
                        </select>
                    </div>

                    <!-- Range Input Fields for Sortable Columns -->
                    {#if filterMode === 'Between'}
                        <!-- Range Input Fields for "Between" Mode -->
                        <div class="range-slider-container">
                            <input
                                type="range"
                                class="range-slider"
                                min={minValue}
                                max={maxValue}
                                bind:value={sliderMin}
                                on:input={handleRangeChange}
                            />
                            <input
                                type="range"
                                class="range-slider"
                                min={minValue}
                                max={maxValue}
                                bind:value={sliderMax}
                                on:input={handleRangeChange}
                            />
                            <div class="range-track" style="right: {sliderRight}%; left: {sliderLeft}%;"></div>
                        </div>
                        <p class="range-values">Between {sliderMin} and {sliderMax}</p>
                    {:else}
                        <div class="range-slider-container">
                            <input
                                type="range"
                                class="range-slider"
                                min={minValue}
                                max={maxValue}
                                bind:value={sliderMin}
                                on:input={handleRangeChange}
                            />
                        </div>
                        <p class="range-values">Equals {sliderMin}</p>
                    {/if}
                {/if}
            </div>
        {/if}
    </div>
</div>

<style>
    button.active {
        font-weight: bold;
    }

    button {
        border: 1px solid #005fcc;
        border-radius: 4px;
        background-color: #005fcc;
        color: #fff;
        font-size: var(--scale-1);
    }

    input[type='checkbox'] {
        width: var(--scale2);
        height: var(--scale2);
        accent-color: #005fcc;
    }

    input[type='text'] {
        width: 100%;
        padding: 0;
        border: 0;
        border-radius: 4px;
        background-color: var(--gray-3);
    }

    input[type='range'] {
        width: 100%;
        border: 0;
        appearance: none;
    }

    input[type='text']:focus-visible,
    input[type='range']:focus-visible {
        outline: 0;
    }

    select {
        width: 100%;
        border: 0;
        border-radius: 4px;
        background-color: #f6f6f6;
    }

    select:focus-visible {
        outline-color: #005fcc;
    }

    ul {
        padding-left: 0;
        list-style-type: none;
    }

    .overlay {
        position: fixed;
        z-index: 999;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        background-color: rgb(0 0 0 / 25%);
        inset: 0;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.3s ease-in-out;
    }

    .overlay.visible {
        opacity: 1;
        pointer-events: all;
    }

    .mobile-filter-menu {
        position: relative;
        width: 100%;
        max-width: 600px;
        max-height: 80vh;
        padding: 1em;
        background: #fff;
        box-shadow: 0 -2px 10px rgb(0 0 0 / 10%);
        color: initial;
        font-size: initial;
        font-weight: initial;
        overflow-y: auto;
        transform: translateY(100%);
        transition: transform 0.3s ease-in-out;
    }

    .mobile-filter-menu.filterable {
        padding-bottom: 0;
    }

    .slide-up {
        transform: translateY(0);
    }

    .slide-down {
        transform: translateY(100%);
    }

    .menu-section {
        margin-top: 16px;
    }

    .menu-section input[type='text'],
    .menu-section select {
        width: 100%;
        border: 0;
        font-size: initial;
    }

    .search-container {
        display: flex;
        align-items: center;
        padding: var(--spacing0);
        border-radius: 4px;
        margin: var(--spacing0) 0;
        background: var(--gray-3);
        gap: var(--spacing0);
    }

    .search-container:focus-within {
        outline: 2px solid #005fcc;
    }

    .select-toggle-button {
        position: relative;
        z-index: 1;
        width: 100%;
        width: fit-content;
        height: 100%;
        padding: 10px;
        border: none;
        border-radius: 4px;
        background: var(--gray-3);
        color: #666;
        cursor: pointer;
        font-size: 16px;
        outline: none;
        transition: color 0.3s;
    }

    .select-toggle-button:disabled {
        pointer-events: none;
    }

    .select-toggle-button:hover:not(.disabled) {
        background: #e0e0e0;
    }

    .select-options-list {
        max-height: 200px;
        padding: var(--spacing0);
        border: 0;
        border-radius: 4px;
        margin: var(--spacing0) 0;
        margin-bottom: 0;
        background: #f6f6f6;
        color: initial;
        list-style-type: none;
        overflow-y: auto;
    }

    .select-options-list li {
        display: flex;
        height: var(--scale4);
        align-items: center;
        gap: var(--spacing-1);
    }

    .close-button {
        padding: 0;
        padding: 4px;
        border: 0;
        border-radius: 4px;
        background: var(--gray-3);
        color: #000;
        cursor: pointer;
        font-size: 18px;
    }

    .close-button:hover {
        background: #e6e6e6;
    }

    .select-toggle-container,
    .sort-toggle-container {
        position: relative;
        display: flex;
        overflow: hidden;
        width: 100%;
        height: auto;
        justify-content: space-between;
        border-radius: 4px;
        margin: var(--spacing0) 0;
        background-color: #f6f6f6;
        user-select: none;
    }

    .select-toggle-container {
        height: auto;
        background: none;
    }

    .sort-toggle-button {
        position: relative;
        z-index: 1;
        width: 100%;
        height: 100%;
        flex: 1;
        padding: 10px;
        border: none;
        border-radius: 4px;
        background: none;
        color: #666;
        cursor: pointer;
        font-size: 16px;
        outline: none;
        transition: color 0.3s;
    }

    .sort-toggle-button:hover {
        background: #e0e0e0;
    }

    .sort-toggle-button:focus {
        border: 2px solid #005fcc;
    }

    .sort-toggle-button.active,
    .sort-toggle-button.active:hover {
        border: 2px solid #005fcc;
        background: #005fcc;
        color: #fff;
        font-weight: 400;
    }

    .range-mode-toggle-container {
        margin: var(--spacing0) 0;
    }

    .range-slider-container {
        position: relative;
        width: 100%;
        height: 40px;
        margin: var(--spacing0) 0;
    }

    .range-slider {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        appearance: none;
        background: none;
        pointer-events: none;
    }

    /* Thumb styling */
    .range-slider::-webkit-slider-thumb {
        position: relative;
        z-index: 3; /* Ensure the thumb is above the track */
        width: 20px;
        height: 20px;
        border: 2px solid #fff;
        border-radius: 50%;
        margin-top: -8px;
        appearance: none;
        background: #005fcc;
        cursor: grab;
        pointer-events: all;
    }

    .range-slider::-moz-range-thumb {
        position: relative;
        z-index: 3;
        width: 20px;
        height: 20px;
        border: 2px solid #fff;
        border-radius: 50%;
        margin-top: -8px;
        background: #005fcc;
        cursor: grab;
        pointer-events: all;
    }

    .range-slider:focus::-webkit-slider-thumb {
        cursor: grabbing;
        outline: 2px solid #005fcc;
    }

    .range-slider:focus::-moz-range-thumb {
        cursor: grabbing;
        outline: 2px solid #005fcc;
    }

    /* The unfilled track */
    .range-slider::-webkit-slider-runnable-track {
        width: 100%;
        height: 5px;
        border: none;
        border-radius: 5px;
        background: #ddd;
    }

    .range-slider::-moz-range-track {
        width: 100%;
        height: 5px;
        border: none;
        border-radius: 5px;
        background: #ddd;
    }

    /* Custom filled track between the thumbs */
    .range-track {
        position: absolute;
        z-index: 1;
        top: 50%;
        right: var(--right);
        left: var(--left);
        height: 5px;
        border-radius: 5px;
        background: #005fcc;
        pointer-events: none;
        transform: translateY(-50%);
    }

    .range-values {
        margin: 0;
        text-align: center;
    }

    @media (prefers-color-scheme: dark) {
        .mobile-filter-menu {
            border-color: var(--cool-90);
            background-color: var(--cool-80);
            color: var(--color-text-body);
        }

        input[type='text'],
        select,
        .close-button,
        .search-container,
        .select-toggle-button,
        .select-options-list,
        .sort-toggle-container,
        .sort-toggle-button {
            background-color: var(--cool-70);
            color: var(--color-text-body);
        }

        .select-toggle-button:hover,
        .sort-toggle-button:hover {
            background-color: var(--cool-60);
        }

        input[type='text']::placeholder {
            color: var(--col-30);
        }

        .range-slider::-webkit-slider-runnable-track {
            background-color: var(--cool-70);
        }
    }
</style>
